import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SPORTS = [
  { v: "rugby_union", l: "Rugby Union" },
  { v: "rugby_league", l: "Rugby League" },
  { v: "afl", l: "AFL" },
  { v: "soccer", l: "Soccer" },
];

const LEVELS = [
  { v: "school", l: "School / Youth" },
  { v: "club", l: "Club / Amateur" },
  { v: "semi_pro", l: "Semi-Professional" },
  { v: "academy", l: "Academy" },
  { v: "pro", l: "Professional" },
  { v: "international", l: "International" },
];
const GOALS = [
  { v: "bulk", l: "Build Mass" },
  { v: "cut", l: "Lean Out" },
  { v: "strength", l: "Max Strength" },
  { v: "speed", l: "Speed & Power" },
  { v: "endurance", l: "Match Fitness" },
  { v: "injury_recovery", l: "Return From Injury" },
];

export default function ProfileSetup() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [sportsData, setSportsData] = useState({});
  const [form, setForm] = useState({
    sport: "rugby_union",
    position: "Prop",
    position_group: "forwards",
    current_level: "club",
    aspirational_level: "semi_pro",
    height_cm: 180,
    weight_kg: 90,
    age: 22,
    training_days_per_week: 4,
    primary_goal: "strength",
    dietary_preference: "omnivore",
    allergies: "",
    next_match_date: "",
    match_week_manual: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sports").then((r) => setSportsData(r.data));
  }, []);

  useEffect(() => {
    if (user?.profile) setForm((f) => ({ ...f, ...user.profile }));
  }, [user]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const setSport = (s) => {
    const groups = sportsData[s]?.groups || {};
    const firstGroup = Object.keys(groups)[0];
    const firstPos = groups[firstGroup]?.[0];
    setForm({ ...form, sport: s, position_group: firstGroup, position: firstPos });
  };

  const setPosition = (p, group) => setForm({ ...form, position: p, position_group: group });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", {
        ...form,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        age: Number(form.age),
        training_days_per_week: Number(form.training_days_per_week),
        next_match_date: form.next_match_date || null,
      });
      await refresh();
      toast.success("Profile saved");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const currentGroups = sportsData[form.sport]?.groups || {};

  return (
    <Layout>
      <div className="p-8 md:p-12 max-w-4xl">
        <div className="mb-8">
          <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Setup</div>
          <h1 className="font-heading font-black uppercase text-5xl mt-2">Your Profile</h1>
          <p className="text-[#A3A3A3] mt-2">The more accurate, the better your AI plans.</p>
        </div>

        <form onSubmit={submit} className="space-y-8">
          {/* Sport */}
          <div className="border border-[#262626] bg-[#121212] p-6">
            <h2 className="font-heading font-bold uppercase text-lg mb-4 accent-underline inline-block">Sport</h2>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map((s) => (
                <button type="button" key={s.v} onClick={() => setSport(s.v)} data-testid={`sport-${s.v}`}
                  className={`px-4 py-3 border font-heading uppercase tracking-widest text-sm ${form.sport === s.v ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {/* Position */}
          <div className="border border-[#262626] bg-[#121212] p-6">
            <h2 className="font-heading font-bold uppercase text-lg mb-4 accent-underline inline-block">Position</h2>
            {Object.entries(currentGroups).map(([group, positions]) => (
              <div key={group} className="mb-4 last:mb-0">
                <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mb-2">{group}</div>
                <div className="flex flex-wrap gap-2">
                  {positions.map((p) => (
                    <button type="button" key={p} onClick={() => setPosition(p, group)} data-testid={`pos-${p}`}
                      className={`px-4 py-2 border font-heading uppercase tracking-wide text-sm ${form.position === p ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="border border-[#262626] bg-[#121212] p-6 grid md:grid-cols-2 gap-6">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Current Level</Label>
              <Select value={form.current_level} onValueChange={(v) => set("current_level", v)}>
                <SelectTrigger className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" data-testid="current-level"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Aspirational Level</Label>
              <Select value={form.aspirational_level} onValueChange={(v) => set("aspirational_level", v)}>
                <SelectTrigger className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" data-testid="aspirational-level"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border border-[#262626] bg-[#121212] p-6 grid md:grid-cols-4 gap-4">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Height (cm)</Label>
              <Input type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)}
                data-testid="height" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Weight (kg)</Label>
              <Input type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)}
                data-testid="weight" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Age</Label>
              <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)}
                data-testid="age" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Days/Week</Label>
              <Input type="number" min={2} max={7} value={form.training_days_per_week} onChange={(e) => set("training_days_per_week", e.target.value)}
                data-testid="training-days" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
          </div>

          <div className="border border-[#262626] bg-[#121212] p-6 grid md:grid-cols-2 gap-6">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Primary Goal</Label>
              <Select value={form.primary_goal} onValueChange={(v) => set("primary_goal", v)}>
                <SelectTrigger className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" data-testid="primary-goal"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {GOALS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Dietary Preference</Label>
              <Select value={form.dietary_preference} onValueChange={(v) => set("dietary_preference", v)}>
                <SelectTrigger className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" data-testid="dietary"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="omnivore">Omnivore</SelectItem>
                  <SelectItem value="vegetarian">Vegetarian</SelectItem>
                  <SelectItem value="vegan">Vegan</SelectItem>
                  <SelectItem value="pescatarian">Pescatarian</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="font-heading uppercase tracking-widest text-xs">Allergies / Foods to Avoid</Label>
              <Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)}
                data-testid="allergies" placeholder="e.g. nuts, shellfish"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
            </div>
          </div>

          {/* Match Week */}
          <div className="border border-[#262626] bg-[#121212] p-6">
            <h2 className="font-heading font-bold uppercase text-lg mb-2 accent-underline inline-block">Match Week</h2>
            <p className="text-xs text-[#A3A3A3] mb-4 mt-3">Set your next match date. Within 7 days, plans auto-taper (35-45% volume cut, intensity preserved).</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Next Match Date</Label>
                <Input type="date" value={form.next_match_date || ""} onChange={(e) => set("next_match_date", e.target.value)}
                  data-testid="next-match-date" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="match-week-toggle">
                  <input type="checkbox" checked={form.match_week_manual} onChange={(e) => set("match_week_manual", e.target.checked)}
                    className="w-5 h-5 accent-[#FF3B30]" />
                  <span className="font-heading uppercase tracking-widest text-sm">Force Match Week Mode</span>
                </label>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={saving} data-testid="save-profile"
            className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-12 px-8">
            {saving ? "Saving…" : "Save & Continue"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
